import { useEffect, useState } from "react";
import { Newspaper, CircleNotch } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { listNews, markNewsSeen } from "@/lib/api";

/**
 * NewsDialog — player-facing announcements viewer.
 *
 * Behavior:
 *   • Opened from the profile dropdown (between "Join Discord" and
 *     "Developer Panel"). Also indicates an unread badge on the trigger
 *     when new posts have been published since the user's
 *     `news_last_seen_at`.
 *   • On open we fetch the full post list newest-first, then POST
 *     `/news/mark-seen` so the unread counter clears immediately.
 *   • The parent is expected to refresh its unread badge via
 *     `onSeen` when this dialog closes.
 */
function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function NewsDialog({ open, onOpenChange, onSeen }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    listNews()
      .then((data) => { if (!cancelled) setPosts(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Mark all posts as seen the moment the dialog opens. Best-effort — a
    // failure here just leaves the badge for the next poll to correct.
    markNewsSeen().then(() => onSeen?.()).catch(() => {});
    return () => { cancelled = true; };
  }, [open, onSeen]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="news-dialog"
        className="rounded-none border border-sky-500/30 bg-[#0B0E14] text-white max-w-2xl p-0 max-h-[88vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-sky-500/20 bg-sky-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-sky-300 flex items-center gap-2">
            <Newspaper size={14} weight="fill" /> News &amp; Updates
            <span className="ml-2 text-zinc-500 text-[10px] tracking-wider font-normal">
              Newest first
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-500 mono text-[11px] uppercase tracking-widest">
              <CircleNotch size={14} className="animate-spin mr-2" /> Loading…
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 mono text-[11px] uppercase tracking-widest text-zinc-500">
              No news posts yet. Check back soon.
            </div>
          ) : (
            posts.map((p) => (
              <article
                key={p.id}
                data-testid={`news-post-${p.id}`}
                className="border border-white/10 bg-black/30 px-4 py-3 space-y-2"
              >
                <header className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sky-200 text-[13px] tracking-wide">
                    {p.title}
                  </h3>
                  <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">
                    {formatDate(p.created_at)}
                  </span>
                </header>
                <p className="text-zinc-300 text-[12px] leading-relaxed whitespace-pre-wrap">
                  {p.body}
                </p>
                {(p.author_name || p.updated_at) && (
                  <footer className="pt-1 mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    {p.author_name && <span>— {p.author_name}</span>}
                    {p.updated_at && <span className="text-zinc-600">· edited {formatDate(p.updated_at)}</span>}
                  </footer>
                )}
              </article>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
